import { useCallback, useEffect, useRef, useState } from "react";
import { Document20Regular, Delete20Regular, ArrowUpload16Regular } from "@fluentui/react-icons";
import { APP_LOCALE } from "../../../config/locale";
import { Modal } from "../../../components/Modal";
import {
  ApiError,
  createWorkDiaryEntry,
  deleteWorkDiaryEntry,
  getWorkDiaryEntries,
  updateWorkDiaryEntry,
  uploadWorkDiaryAttachment,
  type WorkDiaryAttachmentUploadDto,
  type WorkDiaryEntryDto,
} from "../../../workDiary/workDiaryApi";
import {
  formatAttachmentSize,
  isImageAttachment,
  WorkDiaryAttachmentCard,
  WorkDiaryAttachmentThumb,
} from "./WorkDiaryAttachment";

interface WorkDiaryTabProps {
  workId: number;
}

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const toInputDate = (value?: string | null): string => {
  if (!value) return "";
  return value.slice(0, 10);
};

const todayInputDate = (): string => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const sortEntries = (entries: WorkDiaryEntryDto[]): WorkDiaryEntryDto[] =>
  [...entries].sort((a, b) => {
    const byDate = b.entryDate.localeCompare(a.entryDate);
    if (byDate !== 0) return byDate;
    return b.id - a.id;
  });

export function WorkDiaryTab({ workId }: WorkDiaryTabProps) {
  const [entries, setEntries] = useState<WorkDiaryEntryDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<WorkDiaryEntryDto | null>(null);
  const [entryDate, setEntryDate] = useState("");
  const [content, setContent] = useState("");
  const [formAttachments, setFormAttachments] = useState<
    WorkDiaryAttachmentUploadDto[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadEntries = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const vm = await getWorkDiaryEntries(workId);
        setEntries(sortEntries(vm.entries ?? []));
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar o diário de obra.",
        );
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [workId],
  );

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const openCreate = () => {
    setEditingEntry(null);
    setEntryDate(todayInputDate());
    setContent("");
    setFormAttachments([]);
    setError(null);
    setIsModalOpen(true);
  };

  const openEdit = (entry: WorkDiaryEntryDto) => {
    setEditingEntry(entry);
    setEntryDate(toInputDate(entry.entryDate));
    setContent(entry.content ?? "");
    // Seed the form with the existing attachments as items to PRESERVE (id set,
    // no url). Removing one drops it from this list; the server deletes any that
    // are absent from the final `attachments` on save.
    setFormAttachments(
      (entry.attachments ?? []).map((att) => ({
        id: att.id,
        fileName: att.fileName,
        contentType: att.contentType,
        sizeInBytes: att.sizeInBytes,
      })),
    );
    setError(null);
    setIsModalOpen(true);
  };

  const closeForm = () => {
    setIsModalOpen(false);
    setEditingEntry(null);
    setEntryDate("");
    setContent("");
    setFormAttachments([]);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (files.length === 0) return;
    setIsUploading(true);
    setError(null);
    try {
      for (const file of files) {
        const uploaded = await uploadWorkDiaryAttachment(file);
        setFormAttachments((prev) => [...prev, uploaded]);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível carregar o anexo. Apenas imagens e PDF são aceites.",
      );
    } finally {
      setIsUploading(false);
    }
  };

  const removeAttachment = (index: number) => {
    setFormAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (isSaving || isUploading) return;
    if (entryDate === "") {
      setError("A data é obrigatória.");
      return;
    }
    if (!content.trim()) {
      setError("O conteúdo é obrigatório.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      if (editingEntry) {
        const updated = await updateWorkDiaryEntry(editingEntry.id, {
          entryDate,
          content: content.trim(),
          attachments: formAttachments,
        });
        setEntries((prev) =>
          sortEntries(
            prev.map((item) => (item.id === updated.id ? updated : item)),
          ),
        );
      } else {
        const created = await createWorkDiaryEntry({
          workId,
          entryDate,
          content: content.trim(),
          attachments: formAttachments,
        });
        setEntries((prev) => sortEntries([created, ...prev]));
      }
      closeForm();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : editingEntry
            ? "Não foi possível atualizar a entrada do diário."
            : "Não foi possível adicionar a entrada do diário.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (deletingId !== null) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteWorkDiaryEntry(id);
      setEntries((prev) => prev.filter((item) => item.id !== id));
      setConfirmDeleteId(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setEntries((prev) => prev.filter((item) => item.id !== id));
        setConfirmDeleteId(null);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível eliminar a entrada do diário.",
        );
        loadEntries(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2
            className="text-foreground"
            style={{
              fontSize: "var(--text-section-title)",
              fontWeight: "var(--font-weight-semi-bold)",
            }}
          >
            Diário de Obra
          </h2>
          <p className="text-secondary-text text-sm">
            {entries.length} entrada(s)
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Nova entrada
        </button>
      </div>

      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="text-secondary-text text-center py-6">A carregar…</div>
      ) : entries.length === 0 ? (
        <div
          className="bg-card border border-border text-secondary-text text-center px-4 py-8"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          Sem entradas no diário desta obra.
        </div>
      ) : (
        <div className="space-y-4">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="bg-card border border-border p-4"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-foreground font-medium">
                    {formatDate(entry.entryDate)}
                  </div>
                  <div className="text-secondary-text text-sm">
                    {entry.author?.name || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {confirmDeleteId === entry.id ? (
                    <>
                      <span className="text-xs text-secondary-text">Apagar?</span>
                      <button
                        type="button"
                        disabled={deletingId === entry.id}
                        onClick={() => handleDelete(entry.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        {deletingId === entry.id ? "A eliminar…" : "Confirmar"}
                      </button>
                      <button
                        type="button"
                        disabled={deletingId === entry.id}
                        onClick={() => setConfirmDeleteId(null)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => openEdit(entry)}
                        className="text-primary cursor-pointer text-sm"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(entry.id)}
                        className="text-destructive cursor-pointer text-sm"
                      >
                        Eliminar
                      </button>
                    </>
                  )}
                </div>
              </div>

              <p
                className="text-foreground mt-3 break-words"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {entry.content}
              </p>

              {entry.attachments && entry.attachments.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-3 items-start">
                  {entry.attachments.map((att) => (
                    <WorkDiaryAttachmentCard
                      key={att.id}
                      entryId={entry.id}
                      attachment={att}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {isModalOpen && (
        <Modal
          title={editingEntry ? "Editar entrada" : "Nova entrada"}
          onClose={closeForm}
          footer={
            <>
              <button
                type="button"
                onClick={closeForm}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isSaving || isUploading}
                onClick={handleSave}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius)" }}
              >
                {isSaving
                  ? "A guardar…"
                  : editingEntry
                    ? "Guardar"
                    : "Criar entrada"}
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Data *
              <input
                type="date"
                value={entryDate}
                onChange={(event) => setEntryDate(event.target.value)}
                className="px-3 py-2 border border-border rounded bg-background text-foreground"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Conteúdo *
              <textarea
                rows={6}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                maxLength={8000}
                placeholder="Descreva o que aconteceu na obra…"
                className="px-3 py-2 border border-border rounded bg-background text-foreground"
              />
            </label>

            <div className="flex flex-col gap-2 text-sm text-secondary-text md:col-span-2">
              <span>Anexos (imagens ou PDF)</span>
              <label
                className="inline-flex items-center gap-2 px-3 py-2 bg-primary/10 text-primary cursor-pointer w-fit"
                style={{ borderRadius: "var(--radius)" }}
              >
                <ArrowUpload16Regular className="w-4 h-4 shrink-0" />
                <span>Adicionar ficheiros</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  disabled={isUploading}
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              {isUploading && (
                <span className="text-secondary-text text-xs">
                  A carregar anexos…
                </span>
              )}
              {formAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2 items-start">
                  {formAttachments.map((att, index) => {
                    const isExistingImage =
                      att.id !== undefined &&
                      att.id > 0 &&
                      isImageAttachment(att.contentType);
                    return (
                      <div
                        key={att.id ? `id-${att.id}` : `new-${att.fileName}-${index}`}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-accent"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        {isExistingImage && editingEntry ? (
                          <WorkDiaryAttachmentThumb
                            entryId={editingEntry.id}
                            attachmentId={att.id as number}
                            fileName={att.fileName}
                          />
                        ) : (
                          <Document20Regular className="w-4 h-4 text-primary shrink-0" />
                        )}
                        <span className="text-foreground truncate max-w-[200px]">
                          {att.fileName}
                        </span>
                        <span className="text-secondary-text text-xs whitespace-nowrap">
                          {formatAttachmentSize(att.sizeInBytes)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-secondary-text hover:text-destructive cursor-pointer"
                          aria-label="Remover anexo"
                        >
                          <Delete20Regular className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
